import React from 'react';
import Button from '@cvent/carina/components/Button';
import { useSlide } from '@cvent/carina/components/Carousel';
import Avatar from '@components/shared/Avatar';
import useTheme from '@hooks/useTheme';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { LoadingIcon } from '@cvent/carina/components/LoadingIcon';
import useBreakpoints from '@hooks/useBreakpoints';
import { useTranslate } from 'nucleus-text';
import { CSSObject } from '@emotion/react';
import useBlockTheme from '@cvent/blocks/hooks/useTheme';

const useStyles = (): Record<string, CSSObject> => {
  const { font, backgroundColor } = useTheme();

  return {
    container: {
      display: 'flex',
      alignContent: 'center',
      backgroundColor: backgroundColor.base,
      borderRadius: 8,
      wordBreak: 'break-all'
    },
    avatar: {
      flex: 0,
      padding: 24
    },
    name: {
      fontWeight: font.base.weight.bold,
      fontSize: font.base.size.l,
      color: font.color.base,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
        color: font.color.interactive.base,
        cursor: 'pointer'
      },
      paddingRight: 24,
      paddingBottom: 8
    },
    title: {
      fontSize: font.base.size.m,
      color: font.color.soft,
      minHeight: 36,
      paddingRight: 24,
      paddingBottom: 16
    },
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '24px 0'
    },
    buttons: {
      display: 'flex',
      flexWrap: 'wrap',
      button: {
        marginTop: 8
      },
      'button:first-of-type': {
        marginRight: 8
      }
    }
  };
};

function PendingCard(props: {
  contactId: string;
  name: string;
  title?: string;
  profileImageUrl?: string;
  onAccept: React.MouseEventHandler<HTMLButtonElement>;
  onDecline: React.MouseEventHandler<HTMLButtonElement>;
  onSelectDetails: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement>;
  setActiveFocus: boolean;
  loadingAcceptConnection: boolean;
  loadingDeclineConnection: boolean;
  avatarName: string;
}): JSX.Element {
  const {
    contactId,
    name,
    title,
    profileImageUrl,
    onAccept,
    onDecline,
    onSelectDetails,
    setActiveFocus,
    loadingAcceptConnection,
    loadingDeclineConnection,
    avatarName
  } = props;

  const { translate } = useTranslate();
  const { isMobileOrTablet } = useBreakpoints();
  const testID = `pending-connection-card-${contactId}`;
  const styles = useStyles();
  const { setSlideActive } = useSlide();
  const {
    palette: { secondary }
  } = useBlockTheme();

  return (
    // Use carousel setSlideActive function so we can still tab through last set of carousel cards
    <div css={styles.container} onFocus={setActiveFocus ? setSlideActive : null} {...injectTestId(testID)}>
      <div css={styles.avatar}>
        <Avatar
          size={isMobileOrTablet ? 4 : 6}
          text={avatarName}
          image={profileImageUrl}
          borderColorOverride={secondary}
          testID={`${testID}-avatar`}
          onClick={onSelectDetails}
          hoverShadow
        />
      </div>
      <div css={styles.content}>
        <a css={styles.name} {...injectTestId(`${testID}-name`)} onClick={onSelectDetails}>
          {name}
        </a>
        <div css={styles.title} {...injectTestId(`${testID}-title`)}>
          {title}
        </div>
        <div css={styles.buttons}>
          <Button
            appearance="lined"
            text={translate('Connections-Page-Pending-Accept-Button')}
            accessibilityLabel={translate('Connections-Page-Pending-Accept-Button-A11Y', { name })}
            testID={`${testID}-accept-button`}
            onClick={onAccept}
            icon={loadingAcceptConnection ? LoadingIcon : undefined}
            disabled={loadingAcceptConnection || loadingDeclineConnection}
          />
          <Button
            appearance="ghost"
            text={translate('Connections-Page-Pending-Decline-Button')}
            accessibilityLabel={translate('Connections-Page-Pending-Decline-Button-A11Y', { name })}
            testID={`${testID}-decline-button`}
            onClick={onDecline}
            icon={loadingDeclineConnection ? LoadingIcon : undefined}
            disabled={loadingAcceptConnection || loadingDeclineConnection}
          />
        </div>
      </div>
    </div>
  );
}

export default PendingCard;
